import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { holidaysApi, usersApi, leavePoliciesApi } from '../../services/api';
import useStore from '../../store';
import './HolidaysPage.css';

// Matches the app's form-select look (see index.css) so react-select blends
// in with the rest of the form instead of using its default theme.
const policySelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: state.isFocused ? '#7C3AED' : '#E5E7EB',
    boxShadow: state.isFocused ? '0 0 0 3px rgb(124 58 237 / .12)' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#7C3AED' : '#E5E7EB' },
  }),
  multiValue: (base) => ({ ...base, background: '#EDE9FE', borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: '#5B21B6', fontWeight: 600 }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? '#7C3AED' : state.isFocused ? '#F5F0FF' : 'transparent',
    color: state.isSelected ? '#fff' : '#111827',
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Holidays are scoped by which leave policies include them, not by a raw
// region/country field — show the policy name(s) a holiday belongs to.
// `fallbackPolicyName` covers the "My Holidays" view, where each holiday
// comes from a single already-known policy that isn't nested on the record.
function getPolicyBadge(holiday, fallbackPolicyName) {
  const names = (holiday.policies || []).map((p) => p.name);
  if (names.length === 0) {
    if (fallbackPolicyName) return { text: fallbackPolicyName, title: undefined };
    return { text: holiday.type === 'common' ? 'National' : 'Unassigned', title: undefined };
  }
  const text = names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
  return { text, title: names.join(', ') };
}

export default function HolidaysPage() {
  const { user } = useStore();
  const isAdmin = user?.role === 'admin';
  const year = new Date().getFullYear();

  const [holidays, setHolidays] = useState([]);
  const [countries, setCountries] = useState([]);
  const [allPolicies, setAllPolicies] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterPolicyId, setFilterPolicyId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', date: '', type: 'common', policy_ids: [], country_id: '', description: '', is_optional: false });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = { year };
      if (!isAdmin) {
        if (user?.country_id) params.country_id = user.country_id;
        if (user?.region_id) params.region_id = user.region_id;
      }

      const [holRes, countRes, policiesRes] = await Promise.all([
        holidaysApi.list(params),
        usersApi.countries(),
        isAdmin ? leavePoliciesApi.list() : Promise.resolve({ data: { data: [] } }),
      ]);
      setHolidays(holRes.data.data);
      setCountries(countRes.data.data);
      setAllPolicies(policiesRes.data.data || []);
      // Load user's assigned leave policy (if any) and show its holidays/balances
      try {
        if (user?.leave_policy_id) {
          const pol = await leavePoliciesApi.get(user.leave_policy_id);
          setPolicy(pol.data.data);
        } else {
          setPolicy(null);
        }
      } catch (err) {
        // ignore policy fetch errors, fallback to global holidays
        setPolicy(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [year, isAdmin, user?.country_id, user?.region_id, user?.leave_policy_id]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.type === 'regional' && form.policy_ids.length === 0) {
      toast.error('Select at least one leave policy for a regional holiday.');
      return;
    }
    setSaving(true);
    try {
      await holidaysApi.create({
        ...form,
        country_id: form.country_id || null,
        policy_ids: form.type === 'regional' ? form.policy_ids : [],
      });
      toast.success('Holiday added!');
      setShowCreate(false);
      setForm({ name: '', date: '', type: 'common', policy_ids: [], country_id: '', description: '', is_optional: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await holidaysApi.delete(id);
      toast.success('Holiday removed');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const isPolicyMode = !!user?.leave_policy_id && (!isAdmin || filterType === 'my_holidays');
  const sourceHolidays = isPolicyMode ? (policy?.holidays || []) : holidays;

  // Group holidays by month
  const filtered = sourceHolidays.filter((h) => {
    let matchType = true;
    let matchPolicy = true;

    if (filterType !== 'all') {
      if (filterType === 'my_holidays' && isPolicyMode) {
        matchType = true;
      } else if (filterType === 'my_holidays') {
        const userCountry = user?.country_id;
        const userRegion = user?.region_id;
        matchType = (h.type === 'common' && h.country_id === userCountry) || (h.type === 'regional' && h.region_id === userRegion);
      } else {
        matchType = h.type === filterType;
      }
    }

    if (filterPolicyId && !(filterType === 'my_holidays' && isPolicyMode)) {
      matchPolicy = (h.policies || []).some((p) => p.id === filterPolicyId);
    }

    return matchType && matchPolicy;
  });

  const grouped = MONTHS.reduce((acc, _, idx) => {
    const monthHols = filtered.filter((h) => new Date(h.date).getMonth() === idx);
    if (monthHols.length) acc[idx] = monthHols;
    return acc;
  }, {});

  const today = new Date().toISOString().split('T')[0];
  const upcoming = sourceHolidays.find((h) => h.date >= today);

  // Skeleton component for month card
  function SkeletonMonthCard() {
    return (
      <div className="month-group card">
        <div className="month-header">
          <div className="skeleton-line" style={{ width: '60px', height: '16px' }}></div>
          <div className="skeleton-line" style={{ width: '22px', height: '22px', borderRadius: '50%' }}></div>
        </div>
        <ul className="month-holidays">
          {Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="month-holiday-item" style={{ display: 'flex', gap: '12px', padding: '12px 16px', alignItems: 'flex-start' }}>
              <div className="skeleton-line" style={{ width: '44px', height: '44px', flexShrink: 0, borderRadius: '8px' }}></div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div className="skeleton-line" style={{ width: '70%', height: '14px' }}></div>
                <div className="skeleton-line" style={{ width: '50%', height: '12px' }}></div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="holidays-page">
      <div className="page-header">
        <div>
          <div className="page-title">Holidays {year}</div>
          <div className="page-subtitle">{isPolicyMode ? 'Holidays from your assigned leave policy' : 'Company-wide and regional holidays calendar'}</div>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Add Holiday</button>
        )}
      </div>

      {/* Next holiday banner */}
      {upcoming && (
        <div className="next-holiday-banner animate-in">
          <div className="next-holiday-left">
            <span className="next-holiday-tag">Next Holiday</span>
            <h3>{upcoming.name}</h3>
            <p>{new Date(upcoming.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
          <div className="next-holiday-countdown">
            <span className="countdown-num">
              {Math.ceil((new Date(upcoming.date) - new Date()) / (1000 * 60 * 60 * 24))}
            </span>
            <span className="countdown-label">days away</span>
          </div>
        </div>
      )}
      {/* Filter */}
      <div className="holidays-filter animate-in stagger-1" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(isAdmin ? ['all', 'my_holidays', 'common', 'regional'] : ['all', 'common', 'regional']).map((f) => (
            <button
              key={f}
              className={`filter-pill ${filterType === f ? 'filter-pill--active' : ''}`}
              onClick={() => setFilterType(f)}
              disabled={isLoading}
            >
              {f === 'all' ? '🗓 All' : f === 'my_holidays' ? '👤 My Holidays' : f === 'common' ? '🇮🇳 National' : '📍 Regional'}
              <span className="filter-pill-count" style={{ marginLeft: 6 }}>
                {f === 'all' 
                  ? sourceHolidays.length 
                  : f === 'my_holidays'
                    ? sourceHolidays.filter(h => (h.type === 'common' && h.country_id === user?.country_id) || (h.type === 'regional' && h.region_id === user?.region_id)).length
                    : sourceHolidays.filter(h => h.type === f).length}
              </span>
            </button>
          ))}
        </div>
        
        {isAdmin && filterType !== 'my_holidays' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
            <select className="form-select" style={{ minWidth: 220 }} value={filterPolicyId} onChange={(e) => setFilterPolicyId(e.target.value)} disabled={isLoading}>
              <option value="">All Leave Policies</option>
              {allPolicies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Month groups */}
      {isLoading ? (
        <div className="holidays-grid animate-in stagger-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonMonthCard key={`skeleton-month-${i}`} />
          ))}
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">
            {isPolicyMode ? 'No holidays found in your assigned policy' : 'No holidays found'}
          </div>
          <div className="empty-state-text">
            {isPolicyMode ? 'Please contact admin to update your leave policy holidays.' : 'Try changing filters.'}
          </div>
        </div>
      ) : (
      <div className="holidays-grid animate-in stagger-2">
        {Object.entries(grouped).map(([monthIdx, monthHols]) => (
          <div key={monthIdx} className="month-group card">
            <div className="month-header">
              <span className="month-name">{MONTHS[parseInt(monthIdx)]}</span>
              <span className="month-count">{monthHols.length}</span>
            </div>
            <ul className="month-holidays">
              {monthHols.map((h) => {
                const d = new Date(h.date);
                const isPast = h.date < today;
                return (
                  <li key={h.id} className={`month-holiday-item ${isPast ? 'month-holiday-item--past' : ''}`}>
                    <div className="holiday-day-badge">
                      <span>{d.getDate()}</span>
                      <span style={{ fontSize: '0.6rem' }}>{d.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                    </div>
                    <div className="holiday-detail">
                      <span className="holiday-item-name">{h.name}</span>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                        <span className={`badge ${h.type === 'common' ? 'badge-violet' : 'badge-sky'}`} style={{ fontSize: '0.65rem' }} title={getPolicyBadge(h, isPolicyMode ? policy?.name : undefined).title}>
                          {getPolicyBadge(h, isPolicyMode ? policy?.name : undefined).text}
                        </span>
                        {h.is_optional && <span className="badge badge-gray" style={{ fontSize: '0.65rem' }}>Optional</span>}
                      </div>
                    </div>
                    {isAdmin && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--accent-rose)', marginLeft: 'auto', flexShrink: 0 }}
                        onClick={() => handleDelete(h.id)}
                      >
                        ✕
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Holiday</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Holiday Name *</label>
                  <input className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Republic Day" />
                </div>
                <div className="form-grid-2col">
                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Type *</label>
                    <select className="form-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, policy_ids: [] })}>
                      <option value="common">National / Common</option>
                      <option value="regional">Regional / State specific</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Country <span style={{ color: 'red' }}>*</span></label>
                  <select className="form-select" required value={form.country_id} onChange={(e) => setForm({ ...form, country_id: e.target.value })}>
                    <option value="">— Select Country —</option>
                    {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {form.type === 'regional' && (
                  <div className="form-group">
                    <label className="form-label">Leave Policies <span style={{ color: 'red' }}>*</span></label>
                    <Select
                      isMulti
                      styles={policySelectStyles}
                      placeholder="Search leave policies…"
                      noOptionsMessage={() => 'No leave policies available.'}
                      options={allPolicies.map((p) => ({ value: p.id, label: p.name }))}
                      value={allPolicies
                        .filter((p) => form.policy_ids.includes(p.id))
                        .map((p) => ({ value: p.id, label: p.name }))}
                      onChange={(selected) => setForm({ ...form, policy_ids: (selected || []).map((s) => s.value) })}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={form.is_optional} onChange={(e) => setForm({ ...form, is_optional: e.target.checked })} />
                  Optional Holiday
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Add Holiday'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
